import { Controller } from "@hotwired/stimulus"
import Chart from 'chart.js/auto';

// Draws the Figma halftone texture behind the plot area
// (options.plugins.dotGrid). Matches the design's pattern definition:
// 1.2px #81B1D5 dots, 3px pitch, alternate rows staggered by half a tile.
const dotGrid = {
  id: 'dotGrid',
  beforeDatasetsDraw(chart, _args, opts) {
    if (!opts || !opts.color) return

    const { ctx, chartArea } = chart
    const spacing = opts.spacing || 3
    const radius = opts.radius || 0.6
    const { left, top, right, bottom } = chartArea

    ctx.save()
    ctx.beginPath()
    ctx.roundRect(left, top, right - left, bottom - top, 5)
    ctx.clip()
    if (opts.opacity) ctx.globalAlpha = opts.opacity
    ctx.fillStyle = opts.color
    let row = 0
    for (let y = top + radius; y < bottom; y += spacing) {
      const offset = row % 2 === 1 ? spacing / 2 : 0
      for (let x = left + radius + offset; x < right; x += spacing) {
        ctx.beginPath()
        ctx.arc(x, y, radius, 0, Math.PI * 2)
        ctx.fill()
      }
      row++
    }
    ctx.restore()
  }
}

export default class extends Controller {
  static values = {
    config: Object
  }

  connect() {
    const config = this.configValue
    this.applyTickFormats(config)
    this.applyTooltipFormat(config)
    this.applyBarGradient(config)
    this.chart = new Chart(this.element, { ...config, plugins: [dotGrid] })
  }

  disconnect() {
    if (this.chart) this.chart.destroy()
  }

  // Tooltip units via options.plugins.dashboardTooltip = { prefix, suffix, decimals }
  // (callbacks can't travel through the JSON config).
  applyTooltipFormat(config) {
    const opts = config.options?.plugins?.dashboardTooltip
    if (!opts) return

    const tooltip = config.options.plugins.tooltip ||= {}
    const callbacks = tooltip.callbacks ||= {}
    callbacks.label = (context) => {
      const number = context.parsed.y.toLocaleString("en", { maximumFractionDigits: opts.decimals ?? 0 })
      const datasetLabel = context.dataset.label ? `${context.dataset.label}: ` : ""
      return `${datasetLabel}${opts.prefix || ""}${number}${opts.suffix || ""}`
    }
  }

  // Canvas gradients can't travel through the JSON config; components request
  // one via options.plugins.dashboardBarGradient = { from, to }.
  applyBarGradient(config) {
    const gradient = config.options?.plugins?.dashboardBarGradient
    if (!gradient) return

    config.data.datasets.forEach((dataset) => {
      dataset.backgroundColor = (context) => {
        const { ctx, chartArea } = context.chart
        if (!chartArea) return gradient.from

        const fill = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
        fill.addColorStop(0, gradient.from)
        fill.addColorStop(1, gradient.to)
        return fill
      }
    })
  }

  // Tick callbacks can't travel through the JSON config; components request
  // them via options.plugins.dashboardTicks.
  applyTickFormats(config) {
    const format = config.options?.plugins?.dashboardTicks?.y
    if (!format) return

    const yTicks = config.options.scales.y.ticks ||= {}
    if (format === 'percent') {
      yTicks.callback = (value) => `${value}%`
    } else if (format === 'compact') {
      const formatter = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 })
      yTicks.callback = (value) => formatter.format(value)
    }
  }
}
